import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import Sidebar from "@/components/Sidebar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Clock, CheckCircle, CookingPot, Bell, WifiHigh, WifiSlash, SignOut,
  ListBullets, ForkKnife, Wine, ShoppingBag, Truck, Globe, ArrowCounterClockwise, Gear, X
} from "@phosphor-icons/react";
import { formatDistanceToNow } from "date-fns";
import useWebSocket from "@/hooks/useWebSocket";
import { useAuth } from "@/App";
import { useNavigate } from "react-router-dom";
import FullscreenToggle from "@/components/FullscreenToggle";

const FILTERS = [
  { key: "all",      label: "All",      icon: ListBullets },
  { key: "dinein",   label: "Dine In",  icon: ForkKnife },
  { key: "bar",      label: "Bar",      icon: Wine },
  { key: "takeout",  label: "Pick Up",  icon: ShoppingBag },
  { key: "delivery", label: "Delivery", icon: Truck },
  { key: "online",   label: "Online",   icon: Globe },
  { key: "recall",   label: "Recall",   icon: ArrowCounterClockwise },
];

const KitchenPage = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState("all");
  const [showSettings, setShowSettings] = useState(false);
  // Kitchen ticket font scale — driven from Settings → Equipment. Read
  // once on mount; a settings save doesn't need to auto-refresh the
  // KDS terminal (operators reload after config changes anyway).
  const [ticketScale, setTicketScale] = useState("normal");
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/pos-settings`).then(({ data }) => {
      if (!cancelled) setTicketScale((data?.kitchen_ticket_font_scale || "normal").toLowerCase());
    }).catch(() => { /* keep default */ });
    return () => { cancelled = true; };
  }, []);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isKdsOnly = user?.role === "kds";

  // WebSocket for real-time updates
  const wsUrl = `${process.env.REACT_APP_BACKEND_URL?.replace("https://", "wss://").replace("http://", "ws://")}/ws/kitchen`;
  const { lastMessage, isConnected } = useWebSocket(wsUrl);

  useEffect(() => {
    fetchOrders();
    // Poll as fallback every 15 seconds
    const interval = setInterval(fetchOrders, 15000);
    return () => clearInterval(interval);
  }, []);

  // React to WebSocket messages
  useEffect(() => {
    if (lastMessage) {
      if (lastMessage.type === "new_order") {
        toast.info(`New order from ${lastMessage.table_name}!`);
        fetchOrders();
      } else if (lastMessage.type === "order_update") {
        fetchOrders();
      }
    }
  }, [lastMessage]);

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/kitchen/orders`);
      setOrders(response.data);
    } catch (error) {
      console.error("Failed to fetch orders:", error);
    } finally {
      setLoading(false);
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      await axios.put(`${API}/orders/${orderId}/status`, { status: newStatus });
      toast.success(`Order marked as ${newStatus.replace("_", " ")}`);
      fetchOrders();
    } catch (error) {
      toast.error("Failed to update order status");
    }
  };

  const getOrderAge = (createdAt) => {
    const created = new Date(createdAt);
    const now = new Date();
    const diffMins = Math.floor((now - created) / 60000);
    return diffMins;
  };

  const getUrgencyClass = (createdAt) => {
    const mins = getOrderAge(createdAt);
    if (mins > 20) return "border-l-4 border-l-destructive";
    if (mins > 10) return "border-l-4 border-l-yellow-500";
    return "";
  };

  // Categorize orders by source. Best-effort heuristics using available fields.
  const categorize = (o) => {
    const isOnline = o.source === "self-order" || (o.table_name || "").toLowerCase().includes("self");
    if (isOnline) return "online";
    if (o.is_quick_order) {
      // Bar = walk-up no name; Take Out = quick order with name; Delivery = explicit flag
      if (o.delivery) return "delivery";
      if (o.customer_name) return "takeout";
      return "bar";
    }
    if (o.delivery) return "delivery";
    if (o.table_id) return "dinein";
    return "takeout";
  };

  const filterFn = (o) => {
    if (activeFilter === "all") return true;
    if (activeFilter === "recall") return o.status === "ready" || o.status === "served";
    return categorize(o) === activeFilter;
  };

  // Counts per filter for sidebar badges
  const countFor = (key) => {
    if (key === "all") return orders.length;
    if (key === "recall") return orders.filter(o => o.status === "ready" || o.status === "served").length;
    return orders.filter(o => categorize(o) === key).length;
  };

  const visibleOrders = orders.filter(filterFn);
  const pendingOrders = visibleOrders.filter(o => o.status === "pending");
  const inProgressOrders = visibleOrders.filter(o => o.status === "in_progress");
  const readyOrders = visibleOrders.filter(o => o.status === "ready" || o.status === "served");

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading Kitchen Display...</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen bg-background ${
        ticketScale === "small" ? "text-[13px]" :
        ticketScale === "large" ? "text-[17px]" : ""
      }`}
      data-testid="kitchen-page"
      data-ticket-scale={ticketScale}
    >
      {!isKdsOnly && <Sidebar />}

      {/* KDS-specific filter sidebar */}
      <aside
        className={`fixed top-0 ${isKdsOnly ? "left-0" : "left-20 lg:left-64"} h-screen w-12 lg:w-28 bg-card border-r border-border flex flex-col z-30`}
        data-testid="kds-sidebar"
      >
        <div className="p-2 lg:p-3 border-b border-border">
          <div className="flex items-center justify-center lg:justify-start gap-2">
            <CookingPot size={20} className="text-primary" weight="fill" />
            <span className="hidden lg:block font-heading text-sm font-medium">Kitchen</span>
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto p-1.5 space-y-1">
          {FILTERS.map(({ key, label, icon: Icon }) => {
            const active = activeFilter === key;
            const count = countFor(key);
            return (
              <button
                key={key}
                onClick={() => setActiveFilter(key)}
                title={label}
                className={`w-full flex flex-col lg:flex-row items-center lg:gap-2 px-1.5 py-2 rounded-md text-[11px] lg:text-xs transition-colors ${active ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-secondary hover:text-foreground"}`}
                data-testid={`kds-filter-${key}`}
              >
                <Icon size={16} weight={active ? "fill" : "regular"} className="shrink-0" />
                <span className="hidden lg:block flex-1 text-left truncate leading-tight">{label}</span>
                {count > 0 && (
                  <Badge variant="secondary" className="h-4 px-1 text-[9px] mt-0.5 lg:mt-0">{count}</Badge>
                )}
              </button>
            );
          })}
        </nav>
        <div className="p-1.5 border-t border-border space-y-1">
          <button
            onClick={() => setShowSettings(true)}
            title="Settings"
            className="w-full flex flex-col lg:flex-row items-center lg:gap-2 px-1.5 py-2 rounded-md text-[11px] lg:text-xs text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors"
            data-testid="kds-filter-settings"
          >
            <Gear size={16} className="shrink-0" />
            <span className="hidden lg:block">Settings</span>
          </button>
          {isKdsOnly && (
            <button
              onClick={() => { logout(); navigate("/login"); }}
              title="Sign Out"
              className="w-full flex flex-col lg:flex-row items-center lg:gap-2 px-1.5 py-2 rounded-md text-[11px] lg:text-xs text-destructive hover:bg-destructive/10 transition-colors"
              data-testid="kds-sidebar-logout"
            >
              <SignOut size={16} weight="bold" className="shrink-0" />
              <span className="hidden lg:block">Sign Out</span>
            </button>
          )}
        </div>
      </aside>

      <main
        className={
          isKdsOnly
            ? "ml-12 lg:ml-28 p-6"
            : "ml-[128px] lg:ml-[368px] p-6"
        }
      >
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="font-heading text-3xl font-medium text-foreground">
              Kitchen Display{activeFilter !== "all" && ` — ${FILTERS.find(f => f.key === activeFilter)?.label}`}
            </h1>
            <p className="text-muted-foreground">Manage incoming orders</p>
          </div>
          <div className="flex items-center gap-2 text-sm">
            {isConnected ? (
              <Badge variant="outline" className="gap-1 text-green-600 border-green-200 bg-green-50">
                <WifiHigh size={14} weight="bold" /> Live
              </Badge>
            ) : (
              <Badge variant="outline" className="gap-1 text-muted-foreground border-border">
                <WifiSlash size={14} /> Polling
              </Badge>
            )}
            <FullscreenToggle />
          </div>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Pending Orders */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Bell size={24} className="text-primary" weight="fill" />
              <h2 className="font-heading text-xl font-medium">New Orders</h2>
              <Badge variant="secondary" className="ml-auto">{pendingOrders.length}</Badge>
            </div>
            <div className="space-y-4">
              {pendingOrders.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground bg-secondary/30 rounded-xl">
                  No new orders
                </div>
              ) : (
                pendingOrders.map((order, index) => (
                  <OrderTicket
                    key={order.id}
                    order={order}
                    urgencyClass={getUrgencyClass(order.created_at)}
                    onStart={() => updateOrderStatus(order.id, "in_progress")}
                    actionLabel="Start Cooking"
                    actionIcon={<CookingPot size={18} />}
                    style={{ animationDelay: `${index * 0.1}s` }}
                  />
                ))
              )}
            </div>
          </div>
          
          {/* In Progress Orders */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <CookingPot size={24} className="text-accent" weight="fill" />
              <h2 className="font-heading text-xl font-medium">Cooking</h2>
              <Badge variant="secondary" className="ml-auto">{inProgressOrders.length}</Badge>
            </div>
            <div className="space-y-4">
              {inProgressOrders.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground bg-secondary/30 rounded-xl">
                  No orders cooking
                </div>
              ) : (
                inProgressOrders.map((order, index) => (
                  <OrderTicket
                    key={order.id}
                    order={order}
                    urgencyClass={getUrgencyClass(order.created_at)}
                    onStart={() => updateOrderStatus(order.id, "ready")}
                    actionLabel="Mark Ready"
                    actionIcon={<CheckCircle size={18} />}
                    actionVariant="default"
                    style={{ animationDelay: `${index * 0.1}s` }}
                  />
                ))
              )}
            </div>
          </div>
          
          {/* Ready Orders */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <CheckCircle size={24} className="text-green-600" weight="fill" />
              <h2 className="font-heading text-xl font-medium">{activeFilter === "recall" ? "Recall" : "Ready to Serve"}</h2>
              <Badge variant="secondary" className="ml-auto">{readyOrders.length}</Badge>
            </div>
            <div className="space-y-4">
              {readyOrders.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground bg-secondary/30 rounded-xl">
                  No orders ready
                </div>
              ) : (
                readyOrders.map((order, index) => (
                  <OrderTicket
                    key={order.id}
                    order={order}
                    urgencyClass=""
                    onStart={() => updateOrderStatus(order.id, "served")}
                    actionLabel="Mark Served"
                    actionIcon={<CheckCircle size={18} />}
                    actionVariant="outline"
                    style={{ animationDelay: `${index * 0.1}s` }}
                  />
                ))
              )}
            </div>
          </div>
        </div>
      </main>

      {/* KDS Settings Dialog */}
      {showSettings && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setShowSettings(false)}>
          <div className="bg-card rounded-2xl border border-border p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-xl font-medium">Kitchen Display Settings</h3>
              <Button variant="ghost" size="icon" onClick={() => setShowSettings(false)}><X size={20} /></Button>
            </div>
            <div className="space-y-3 text-sm text-muted-foreground">
              <p>
                Display options for this kitchen station. More controls (station routing, alert sounds, color rules)
                can be added here later.
              </p>
              <ul className="list-disc list-inside space-y-1.5 text-foreground/80">
                <li>Live updates: <span className="font-medium">{isConnected ? "Connected (WebSocket)" : "Polling fallback"}</span></li>
                <li>Filter active: <span className="font-medium">{FILTERS.find(f => f.key === activeFilter)?.label || activeFilter}</span></li>
                <li>Total visible orders: <span className="font-medium">{visibleOrders.length}</span></li>
              </ul>
            </div>

            {isKdsOnly && (
              <div className="mt-5 pt-4 border-t border-border">
                <KioskAutoLoginToggle user={user} />
              </div>
            )}

            <div className="mt-5 flex justify-end">
              <Button onClick={() => setShowSettings(false)} data-testid="kds-settings-close">Close</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


// KioskAutoLoginToggle — lets a KDS user opt this physical device into
// auto-login mode. Stores the KDS PIN + name in localStorage, which is
// device-scoped (never sent to the server). LoginPage reads this on mount
// and offers a one-tap "Boot as [name]" button.
//
// Security posture: the PIN sits in plaintext on the device. Acceptable ONLY
// for locked-down kitchen kiosks where physical access = intended access.
// The banner in the UI makes the trade-off explicit.
const KIOSK_STORAGE_KEY = "kdsKioskLogin";

const KioskAutoLoginToggle = ({ user }) => {
  const [enabled, setEnabled] = useState(() => !!localStorage.getItem(KIOSK_STORAGE_KEY));
  const [showPinPrompt, setShowPinPrompt] = useState(false);
  const [pin, setPin] = useState("");
  const [verifying, setVerifying] = useState(false);

  const handleDisable = () => {
    localStorage.removeItem(KIOSK_STORAGE_KEY);
    setEnabled(false);
    toast.success("Kiosk auto-login disabled for this device.");
  };

  const handleVerifyAndEnable = async () => {
    if (pin.length !== 4) return;
    setVerifying(true);
    try {
      // Verify by attempting a fresh login. We do NOT swap the current
      // session — we only care whether the PIN belongs to THIS KDS user.
      const res = await axios.post(`${API}/auth/login`, { pin, skip_clock_in: true });
      if (res.data?.user?.id !== user.id) {
        toast.error("That PIN belongs to a different account.");
        return;
      }
      localStorage.setItem(
        KIOSK_STORAGE_KEY,
        JSON.stringify({
          pin,
          name: user.name,
          user_id: user.id,
          enabledAt: new Date().toISOString(),
        })
      );
      setEnabled(true);
      setShowPinPrompt(false);
      setPin("");
      toast.success(`Kiosk auto-login enabled — this device will boot as “${user.name}”.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "PIN verification failed.");
      setPin("");
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div data-testid="kiosk-autologin-section">
      <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">
        Kiosk Auto-Login
      </div>

      {!enabled && !showPinPrompt && (
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground">
            Bolted-down kitchen tablet? Enable auto-login so this device boots straight into
            the Kitchen Display after any reboot — no PIN entry needed.
          </p>
          <Button
            variant="outline"
            className="w-full gap-2"
            onClick={() => setShowPinPrompt(true)}
            data-testid="kiosk-enable-btn"
          >
            Enable Kiosk Auto-Login
          </Button>
        </div>
      )}

      {enabled && (
        <div className="space-y-2">
          <div className="rounded-md border border-green-200 bg-green-50 dark:bg-green-950/30 dark:border-green-900 px-3 py-2 text-sm text-green-800 dark:text-green-300 flex items-center gap-2">
            <CheckCircle size={16} weight="fill" />
            <span>
              Enabled — this device auto-boots as <span className="font-semibold">{user.name}</span>.
            </span>
          </div>
          <Button
            variant="outline"
            className="w-full"
            onClick={handleDisable}
            data-testid="kiosk-disable-btn"
          >
            Disable Kiosk Auto-Login
          </Button>
        </div>
      )}

      {showPinPrompt && (
        <div className="space-y-3">
          <div className="rounded-md border border-amber-200 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-900 px-3 py-2 text-xs text-amber-900 dark:text-amber-200">
            <strong>Heads up:</strong> your PIN will be saved on this device (in browser storage)
            so it can log itself back in. Only enable this on a locked-down kitchen tablet.
          </div>
          <div>
            <label className="text-xs uppercase tracking-wide text-muted-foreground">
              Re-enter your PIN to confirm
            </label>
            <input
              type="password"
              inputMode="numeric"
              maxLength={4}
              autoFocus
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
              className="mt-1 w-full h-12 rounded-md border border-border bg-background text-center text-2xl font-mono tracking-[10px]"
              placeholder="••••"
              data-testid="kiosk-pin-input"
            />
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => { setShowPinPrompt(false); setPin(""); }}
              disabled={verifying}
            >
              Cancel
            </Button>
            <Button
              className="flex-1"
              onClick={handleVerifyAndEnable}
              disabled={pin.length !== 4 || verifying}
              data-testid="kiosk-verify-btn"
            >
              {verifying ? "Verifying…" : "Confirm & Enable"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};


const OrderTicket = ({ order, urgencyClass, onStart, actionLabel, actionIcon, actionVariant = "secondary", style }) => {
  const orderAge = Math.floor((new Date() - new Date(order.created_at)) / 60000);
  const isPublicOrder = ["pickup", "delivery"].includes((order.fulfillment_type || "").toLowerCase());
  const notifiableStatus = ["in_progress", "ready"].includes((order.status || "").toLowerCase());
  const canNotify = isPublicOrder && notifiableStatus && (order.customer_phone || order.customer_email);
  const [notifying, setNotifying] = useState(false);

  const notifyCustomer = async () => {
    setNotifying(true);
    try {
      const res = await axios.post(`${API}/orders/${order.id}/notify-customer`);
      const { sms, email, skipped } = res.data.result || {};
      if (skipped) toast.info(`Not sent: ${skipped}`);
      else {
        const parts = [];
        if (sms) parts.push(`SMS ${sms[0] ? "sent" : "failed"}`);
        if (email) parts.push(`Email ${email[0] ? "sent" : "failed"}`);
        toast.success(parts.join(" · ") || "Notification triggered");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to notify");
    } finally { setNotifying(false); }
  };

  return (
    <div 
      className={`kitchen-ticket rounded-lg p-4 animate-fade-in-up ${urgencyClass}`}
      style={style}
      data-testid={`kitchen-order-${order.id}`}
    >
      <div className="flex items-center justify-between mb-3">
        <div>
          <span className="font-heading font-medium text-lg">{order.table_name}</span>
          <span className="text-muted-foreground text-sm ml-2">#{order.id.slice(-6)}</span>
          {isPublicOrder && (
            <Badge variant="outline" className="ml-2 text-[10px] capitalize" data-testid={`fulfillment-badge-${order.id}`}>
              {order.fulfillment_type}
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-1 text-sm">
          <Clock size={14} className={orderAge > 15 ? "text-destructive" : "text-muted-foreground"} />
          <span className={orderAge > 15 ? "text-destructive font-medium" : "text-muted-foreground"}>
            {orderAge}m
          </span>
        </div>
      </div>
      
      <div className="space-y-2 mb-4">
        {order.items.map((item, idx) => (
          <div key={idx} className="flex items-start gap-2">
            <span className="font-mono text-sm bg-secondary px-2 py-0.5 rounded">
              {item.quantity}x
            </span>
            <div className="flex-1">
              <p className="font-medium text-sm">{item.menu_item_name}</p>
              {item.modifiers && item.modifiers.length > 0 && (
                <p className="text-xs text-muted-foreground">
                  {item.modifiers.map(m => m.name).join(", ")}
                </p>
              )}
              {item.notes && (
                <p className="text-xs text-primary italic">Note: {item.notes}</p>
              )}
            </div>
          </div>
        ))}
      </div>
      
      <div className="flex items-center justify-between pt-3 border-t border-border gap-2 flex-wrap">
        <span className="text-xs text-muted-foreground">
          {isPublicOrder ? (order.customer_name || "Guest") : `Server: ${order.server_name}`}
        </span>
        <div className="flex gap-2">
          {canNotify && (
            <Button
              size="sm"
              variant="outline"
              onClick={notifyCustomer}
              disabled={notifying}
              className="gap-1"
              data-testid={`notify-customer-${order.id}`}
            >
              <Bell size={14} />
              {notifying ? "Sending..." : "Notify"}
            </Button>
          )}
          <Button 
            size="sm" 
            variant={actionVariant}
            onClick={onStart}
            className="click-effect gap-1"
            data-testid={`order-action-${order.id}`}
          >
            {actionIcon}
            {actionLabel}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default KitchenPage;
